import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTheme } from "@mui/material/styles";

import {
  Add,
  Edit,
  Delete,
  Refresh,
  Close,
  Save,
  Search,
  FilterList,
  PlaylistAdd as BulkAllocateIcon,
} from "@mui/icons-material";

import {
  Alert,
  Box,
  Button,
  Select,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { useBranch } from "./../../layouts/BranchContext";

/* =========================================================
   API
========================================================= */

const API_BASE_URL = "https://wims.revisetoexcel.com/api";

const ENDPOINTS = {
  list: "/hr/leave-balances/list/",
  create: "/hr/leave-balances/",
  bulkCreate: "/hr/leave-balances/bulk-create/",
  detail: (id) => `/hr/leave-balances/${id}/`,
  update: (id) => `/hr/leave-balances/${id}/update/`,
  status: (id) => `/hr/leave-balances/${id}/status/`,
  delete: (id) => `/hr/leave-balances/${id}/delete/`,
  leaveTypesDropdown: "/hr/leave-types/dropdown/",
  employeesFilter: "/staff/employees/filter/",
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/* =========================================================
   ACCESS TOKEN
========================================================= */

const getAccessToken = () => {
  if (typeof window === "undefined") return null;

  const tokenKeys = ["accessToken", "access_token", "token"];

  for (const key of tokenKeys) {
    const localToken = localStorage.getItem(key);

    if (localToken) return localToken;

    const sessionToken = sessionStorage.getItem(key);

    if (sessionToken) return sessionToken;
  }

  return null;
};

/* =========================================================
   AXIOS INTERCEPTOR
========================================================= */

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* =========================================================
   HELPERS
========================================================= */

const getStoredUser = () => {
  if (typeof window === "undefined") return {};

  try {
    return JSON.parse(
      sessionStorage.getItem("user") ||
        localStorage.getItem("user") ||
        "{}"
    );
  } catch (error) {
    console.error("Unable to read logged-in user:", error);

    return {};
  }
};

const normalizeId = (value) => {
  if (value === null || value === undefined || value === "") return "";

  if (typeof value === "object") {
    return normalizeId(
      value.id ??
        value.pk ??
        value.value ??
        value.branch_id ??
        value.organization_id
    );
  }

  return String(value);
};

const normalizeArray = (data) => {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.results)) return data.results;

  if (Array.isArray(data?.data)) return data.data;

  if (Array.isArray(data?.items)) return data.items;

  return [];
};

const getErrorMessage = (error) => {
  if (error?.response?.status === 401) {
    return "Unauthorized. Please login again.";
  }

  if (error?.response?.data?.detail) {
    return error.response.data.detail;
  }

  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.response?.data?.error) {
    return error.response.data.error;
  }

  if (error?.response?.data) {
    const data = error.response.data;

    if (typeof data === "string") return data;

    return Object.values(data).flat().join(" ");
  }

  return error?.message || "Something went wrong.";
};

/* =========================================================
   COMPONENT
========================================================= */

function LeaveBalance() {
  const theme = useTheme();

  const { selectedBranchId } = useBranch();

  const currentUser = useMemo(() => getStoredUser(), []);

  const organizationId = normalizeId(
    currentUser?.organization_id ??
      currentUser?.organization?.id ??
      currentUser?.organization
  );

  const isDark = theme.palette.mode === "dark";
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.disabled;
  const borderColor = theme.palette.divider;
  const inputBackground = theme.palette.background.paper;

  /* =======================================================
     DATA
  ======================================================= */

  const [balances, setBalances] = useState([]);

  const [employees, setEmployees] = useState([]);

  const [leaveTypes, setLeaveTypes] = useState([]);

  /* =======================================================
     LOADING
  ======================================================= */

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  /* =======================================================
     FILTER MODAL
  ======================================================= */

  const [filterModalOpen, setFilterModalOpen] = useState(false);

  /* =======================================================
     FILTERS
  =======================================================
     Branch comes from the global selector (useBranch).
     The list endpoint supports: organization, branch,
     employee, leave_type, year — there is no is_active or
     search param, so status filtering and search stay
     client-side.
  ======================================================= */

  const [filterStatus, setFilterStatus] = useState("");

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [searchText, setSearchText] = useState("");

  /* =======================================================
     PAGINATION
  ======================================================= */

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* =======================================================
     CREATE / EDIT
  ======================================================= */

  const [openDialog, setOpenDialog] = useState(false);

  const [editMode, setEditMode] = useState(false);

  const [selectedBalanceId, setSelectedBalanceId] = useState(null);

  /* =======================================================
     FORM
  ======================================================= */

  const [formData, setFormData] = useState({
    employee: "",
    leave_type: "",
    year: new Date().getFullYear(),
    allocated_days: "",
  });

  /* =======================================================
     EMPLOYEE MODAL
  ======================================================= */

  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);

  const [employeeSearch, setEmployeeSearch] = useState("");

  /* =======================================================
     BULK ALLOCATE MODAL
  ======================================================= */

  const [bulkModalOpen, setBulkModalOpen] = useState(false);

  const [bulkForm, setBulkForm] = useState({
    leave_type: "",
    year: new Date().getFullYear(),
    allocated_days: "",
  });

  /* =======================================================
     STATUS CONFIRMATION
  ======================================================= */

  const [statusModalOpen, setStatusModalOpen] = useState(false);

  const [statusBalance, setStatusBalance] = useState(null);

  const [statusSaving, setStatusSaving] = useState(false);

  /* =======================================================
     DELETE
  ======================================================= */

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [deleteId, setDeleteId] = useState(null);

  /* =======================================================
     SNACKBAR
  ======================================================= */

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* =======================================================
     YEARS
  ======================================================= */

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();

    return Array.from({ length: 7 }, (_, index) => currentYear - 3 + index);
  }, []);

  /* =======================================================
     COMMON DIALOG STYLE
  ======================================================= */

  const dialogPaperStyles = {
    backgroundColor: theme.palette.background.paper,
    color: textPrimary,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: isDark
      ? "0 20px 50px rgba(0,0,0,0.45)"
      : "0 20px 50px rgba(0,0,0,0.12)",
  };

  /* =======================================================
     SELECT STYLE
  ======================================================= */

  const selectStyles = {
    borderRadius: 2,
    backgroundColor: inputBackground,
    color: textPrimary,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "& .MuiSvgIcon-root": {
      color: textPrimary,
    },
  };

  /* =======================================================
     MENU STYLE
  ======================================================= */

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        color: textPrimary,
        border: `1px solid ${theme.palette.divider}`,
        "& .MuiMenuItem-root": {
          color: textPrimary,
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "&.Mui-selected": {
            backgroundColor: isDark
              ? "rgba(255,255,255,0.12)"
              : "rgba(0,0,0,0.08)",
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(255,255,255,0.16)"
                : "rgba(0,0,0,0.12)",
            },
          },
        },
      },
    },
  };

  /* =======================================================
     TEXT FIELD STYLE
  ======================================================= */

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      color: textPrimary,
      backgroundColor: theme.palette.background.paper,
      "& fieldset": {
        borderColor,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputBase-input": {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
    },
    "& .MuiInputBase-input::placeholder": {
      color: textPrimary,
      opacity: 0.8,
    },
  };

  /* =======================================================
     SNACKBAR
  ======================================================= */

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  /* =======================================================
     FETCH BALANCES
  ======================================================= */

  const fetchBalances = useCallback(
    async (yearOverride) => {
      if (!organizationId) return;

      setLoading(true);

      try {
        const params = {
          organization: organizationId,
          year: yearOverride ?? selectedYear,
        };

        const normalizedBranch = normalizeId(selectedBranchId);

        if (normalizedBranch) {
          params.branch = normalizedBranch;
        }

        const response = await api.get(ENDPOINTS.list, { params });

        setBalances(normalizeArray(response.data));
      } catch (error) {
        console.error("Leave Balance List Error:", error);

        showSnackbar(getErrorMessage(error), "error");
      } finally {
        setLoading(false);
      }
    },
    [organizationId, selectedBranchId, selectedYear]
  );

  /* =======================================================
     FETCH EMPLOYEES
  ======================================================= */

  const fetchEmployees = useCallback(async () => {
    if (!organizationId) return;

    try {
      const params = {
        organization_id: organizationId,
      };

      const normalizedBranch = normalizeId(selectedBranchId);

      if (normalizedBranch) {
        params.branch_id = normalizedBranch;
      }

      const response = await api.get(ENDPOINTS.employeesFilter, { params });

      setEmployees(normalizeArray(response.data));
    } catch (error) {
      console.error("Employee API Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    }
  }, [organizationId, selectedBranchId]);

  /* =======================================================
     FETCH LEAVE TYPES
  ======================================================= */

  const fetchLeaveTypes = useCallback(async () => {
    if (!organizationId) return;

    try {
      const params = {
        organization: organizationId,
      };

      const normalizedBranch = normalizeId(selectedBranchId);

      if (normalizedBranch) {
        params.branch = normalizedBranch;
      }

      const response = await api.get(ENDPOINTS.leaveTypesDropdown, { params });

      setLeaveTypes(normalizeArray(response.data));
    } catch (error) {
      console.error("Leave Type API Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    }
  }, [organizationId, selectedBranchId]);

  /* =======================================================
     INITIAL DATA
  ======================================================= */

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    fetchLeaveTypes();
  }, [fetchLeaveTypes]);

  /* =======================================================
     SEARCH + STATUS FILTER (CLIENT-SIDE)
  ======================================================= */

  const filteredBalances = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    return balances.filter((balance) => {
      /* STATUS — the list endpoint has no is_active param */

      if (filterStatus !== "") {
        const isActive = Boolean(balance.is_active);

        const matchesStatus =
          (filterStatus === "true" && isActive) ||
          (filterStatus === "false" && !isActive);

        if (!matchesStatus) return false;
      }

      if (!search) return true;

      const employeeName = balance.employee_name || "";
      const employeeCode = balance.employee_code || "";
      const leaveType = balance.leave_type_name || "";
      const leaveCode = balance.leave_code || "";
      const branch = balance.branch_name || String(balance.branch || "");

      return (
        employeeName.toLowerCase().includes(search) ||
        employeeCode.toLowerCase().includes(search) ||
        leaveType.toLowerCase().includes(search) ||
        leaveCode.toLowerCase().includes(search) ||
        branch.toLowerCase().includes(search)
      );
    });
  }, [balances, searchText, filterStatus]);

  /* =======================================================
     PAGINATION DATA
  ======================================================= */

  const displayedBalances = filteredBalances.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* =======================================================
     OPEN CREATE
  ======================================================= */

  const handleOpenCreate = () => {
    setEditMode(false);

    setSelectedBalanceId(null);

    setFormData({
      employee: "",
      leave_type: "",
      year: selectedYear,
      allocated_days: "",
    });

    setOpenDialog(true);
  };

  /* =======================================================
     OPEN EDIT
  ======================================================= */

  const handleOpenEdit = (balance) => {
    setEditMode(true);

    setSelectedBalanceId(balance.id);

    setFormData({
      employee: balance.employee ?? "",
      leave_type: balance.leave_type ?? "",
      year: balance.year ?? selectedYear,
      allocated_days: balance.allocated_days ?? "",
    });

    setOpenDialog(true);
  };

  /* =======================================================
     CLOSE FORM
  ======================================================= */

  const handleCloseDialog = () => {
    if (saving) return;

    setOpenDialog(false);

    setSelectedBalanceId(null);

    setEditMode(false);
  };

  /* =======================================================
     FORM CHANGE
  ======================================================= */

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =======================================================
     VALIDATION
  ======================================================= */

  const validateForm = () => {
    if (!formData.employee) {
      showSnackbar("Employee is required.", "error");

      return false;
    }

    if (!formData.leave_type) {
      showSnackbar("Leave type is required.", "error");

      return false;
    }

    if (!formData.year) {
      showSnackbar("Year is required.", "error");

      return false;
    }

    if (
      formData.allocated_days === "" ||
      Number.isNaN(Number(formData.allocated_days)) ||
      Number(formData.allocated_days) < 0
    ) {
      showSnackbar("Allocated days must be a valid non-negative number.", "error");

      return false;
    }

    return true;
  };

  /* =======================================================
     CREATE
  =======================================================
     Payload matches the Swagger create body exactly:
     { employee, leave_type, year, allocated_days }
     allocated_days is $decimal — sent as a string.
  ======================================================= */

  const handleCreate = async () => {
    if (!validateForm()) return;

    setSaving(true);

    try {
      const payload = {
        employee: Number(formData.employee),
        leave_type: Number(formData.leave_type),
        year: Number(formData.year),
        allocated_days: String(formData.allocated_days),
      };

      await api.post(ENDPOINTS.create, payload);

      showSnackbar("Leave balance created successfully.", "success");

      setOpenDialog(false);

      setFormData({
        employee: "",
        leave_type: "",
        year: selectedYear,
        allocated_days: "",
      });

      await fetchBalances();
    } catch (error) {
      console.error("Create Leave Balance Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     UPDATE
  =======================================================
     Same body as create, against PUT /{id}/update/.
  ======================================================= */

  const handleUpdate = async () => {
    if (!validateForm()) return;

    if (!selectedBalanceId) return;

    setSaving(true);

    try {
      const payload = {
        employee: Number(formData.employee),
        leave_type: Number(formData.leave_type),
        year: Number(formData.year),
        allocated_days: String(formData.allocated_days),
      };

      await api.put(ENDPOINTS.update(selectedBalanceId), payload);

      showSnackbar("Leave balance updated successfully.", "success");

      setOpenDialog(false);

      /* Refresh the edited row from GET /{id}/ so read-only
         fields (used_days, remaining_days, names) come from
         the API rather than client guesses. */

      try {
        const detailResponse = await api.get(
          ENDPOINTS.detail(selectedBalanceId)
        );

        const updated = detailResponse.data;

        setBalances((prev) =>
          prev.map((balance) =>
            balance.id === selectedBalanceId
              ? { ...balance, ...updated }
              : balance
          )
        );
      } catch (detailError) {
        console.error("Balance detail refresh failed:", detailError);

        await fetchBalances();
      }
    } catch (error) {
      console.error("Update Leave Balance Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     SAVE
  ======================================================= */

  const handleSave = () => {
    if (editMode) {
      handleUpdate();
    } else {
      handleCreate();
    }
  };

  /* =======================================================
     BULK ALLOCATE
  =======================================================
     POST /hr/leave-balances/bulk-create/
     { organization, branch, leave_type, year, allocated_days }
     allocated_days is a number here per the Swagger body.
  ======================================================= */

  const handleOpenBulkModal = () => {
    setBulkForm({
      leave_type: "",
      year: selectedYear,
      allocated_days: "",
    });

    setBulkModalOpen(true);
  };

  const handleBulkFormChange = (event) => {
    const { name, value } = event.target;

    setBulkForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateBulkForm = () => {
    if (!bulkForm.leave_type) {
      showSnackbar("Leave type is required.", "error");

      return false;
    }

    if (!bulkForm.year) {
      showSnackbar("Year is required.", "error");

      return false;
    }

    if (
      bulkForm.allocated_days === "" ||
      Number.isNaN(Number(bulkForm.allocated_days)) ||
      Number(bulkForm.allocated_days) < 0
    ) {
      showSnackbar("Allocated days must be a valid non-negative number.", "error");

      return false;
    }

    if (!organizationId || !normalizeId(selectedBranchId)) {
      showSnackbar("Organization and branch are required.", "error");

      return false;
    }

    return true;
  };

  const handleBulkAllocate = async () => {
    if (!validateBulkForm()) return;

    setSaving(true);

    try {
      const payload = {
        organization: Number(organizationId),
        branch: Number(normalizeId(selectedBranchId)),
        leave_type: Number(bulkForm.leave_type),
        year: Number(bulkForm.year),
        allocated_days: Number(bulkForm.allocated_days),
      };

      await api.post(ENDPOINTS.bulkCreate, payload);

      showSnackbar(
        "Leave balance allocated to all active employees successfully.",
        "success"
      );

      setBulkModalOpen(false);

      await fetchBalances();
    } catch (error) {
      console.error("Bulk Allocate Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     EMPLOYEE MODAL
  ======================================================= */

  const filteredEmployees = useMemo(() => {
    const search = employeeSearch.trim().toLowerCase();

    if (!search) return employees;

    return employees.filter((employee) => {
      const name =
        employee.name || employee.employee_name || employee.full_name || "";
      const code = employee.employee_code || "";
      const department = employee.department_name || "";
      const designation = employee.designation_name || "";
      const branch = employee.branch_name || "";

      return (
        name.toLowerCase().includes(search) ||
        code.toLowerCase().includes(search) ||
        department.toLowerCase().includes(search) ||
        designation.toLowerCase().includes(search) ||
        branch.toLowerCase().includes(search)
      );
    });
  }, [employees, employeeSearch]);

  const handleOpenEmployeeModal = () => {
    setEmployeeSearch("");
    setEmployeeModalOpen(true);
  };

  const handleCloseEmployeeModal = () => {
    setEmployeeModalOpen(false);
  };

  const handleSelectEmployee = (employee) => {
    setFormData((prev) => ({
      ...prev,
      employee: employee.id,
    }));

    setEmployeeModalOpen(false);
  };

  /* =======================================================
     STATUS CONFIRMATION
  ======================================================= */

  const handleOpenStatusConfirmation = (balance) => {
    setStatusBalance(balance);
    setStatusModalOpen(true);
  };

  const handleCloseStatusConfirmation = () => {
    if (statusSaving) return;

    setStatusBalance(null);
    setStatusModalOpen(false);
  };

  const handleConfirmStatusChange = async () => {
    if (!statusBalance) return;

    const newStatus = !statusBalance.is_active;

    setStatusSaving(true);

    try {
      await api.patch(ENDPOINTS.status(statusBalance.id), {
        is_active: newStatus,
      });

      showSnackbar(
        `Leave balance ${newStatus ? "activated" : "deactivated"} successfully.`,
        "success"
      );

      /* Update the row locally — no full reload needed. */

      setBalances((prev) =>
        prev.map((balance) =>
          balance.id === statusBalance.id
            ? { ...balance, is_active: newStatus }
            : balance
        )
      );

      setStatusModalOpen(false);
      setStatusBalance(null);
    } catch (error) {
      console.error("Status Update Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setStatusSaving(false);
    }
  };

  /* =======================================================
     DELETE
  ======================================================= */

  const handleOpenDelete = (id) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDelete = () => {
    if (saving) return;

    setDeleteId(null);
    setOpenDeleteDialog(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setSaving(true);

    try {
      await api.delete(ENDPOINTS.delete(deleteId));

      showSnackbar("Leave balance deleted successfully.", "success");

      setOpenDeleteDialog(false);
      setDeleteId(null);

      await fetchBalances();
    } catch (error) {
      console.error("Delete Leave Balance Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     FILTER RESET
  ======================================================= */

  const handleResetFilter = () => {
    const currentYear = new Date().getFullYear();

    setFilterStatus("");
    setSearchText("");
    setSelectedYear(currentYear);
    setPage(0);
    setFilterModalOpen(false);

    fetchBalances(currentYear);
  };

  /* =======================================================
     PAGINATION
  ======================================================= */

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));

    setPage(0);
  };

  /* =======================================================
     DISPLAY HELPERS
  ======================================================= */

  const getEmployeeLabel = (employee) => {
    return (
      employee.name ||
      employee.employee_name ||
      employee.full_name ||
      employee.employee_code ||
      `Employee ${employee.id}`
    );
  };

  const getLeaveTypeLabel = (leaveType) => {
    return (
      leaveType.leave_name ||
      leaveType.name ||
      leaveType.leave_type_name ||
      leaveType.label ||
      `Leave Type ${leaveType.id}`
    );
  };

  /* =======================================================
     UI
  ======================================================= */

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: "100vh",
        color: textPrimary,
      }}
    >
      {/* ===================================================
          PAGE HEADER
      =================================================== */}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        {/* SEARCH — client-side, the list endpoint has no
            search param */}

        <TextField
          size="small"
          variant="outlined"
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
            setPage(0);
          }}
          placeholder="Search employee, leave type..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: theme.palette.primary.main }} />
              </InputAdornment>
            ),
            endAdornment:
              searchText && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchText("");
                      setPage(0);
                    }}
                    sx={{ color: textPrimary }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
          }}
          sx={{
            width: { xs: "100%", sm: 340, md: 360 },
            "& .MuiOutlinedInput-root": {
              height: 50,
              borderRadius: "30px",
              backgroundColor: inputBackground,
              color: textPrimary,
              "& input": {
                color: textPrimary,
                WebkitTextFillColor: textPrimary,
              },
              "& input::placeholder": {
                color: textPrimary,
                opacity: 0.85,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: isDark
                  ? "rgba(255,255,255,0.28)"
                  : "rgba(0,0,0,0.22)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: 2,
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />

        {/* ACTIONS */}

        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: { xs: "flex-start", md: "flex-end" },
          }}
        >
          {/* REFRESH */}

          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={() => fetchBalances()}
            disabled={loading}
            sx={{
              height: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          >
            Refresh
          </Button>

          {/* FILTER */}

          <Button
            variant="contained"
            startIcon={<FilterList />}
            onClick={() => setFilterModalOpen(true)}
            sx={{
              height: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          >
            Filters
          </Button>

          {/* BULK ALLOCATE */}

          <Button
            variant="contained"
            startIcon={<BulkAllocateIcon />}
            onClick={handleOpenBulkModal}
            sx={{
              height: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          >
            Bulk Allocate
          </Button>

          {/* ADD */}

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={{
              height: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          >
            Add Balance
          </Button>
        </Box>
      </Box>

      {/* ===================================================
          FILTER MODAL
      =================================================== */}

      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.5,
            color: textPrimary,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: textPrimary }}>
              Filter Leave Balances
            </Typography>

            <IconButton
              size="small"
              onClick={() => setFilterModalOpen(false)}
              sx={{
                color: textPrimary,
                border: `1px solid ${theme.palette.divider}`,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent
          sx={{
            px: 3,
            py: 3,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            {/* YEAR — server-side param */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                YEAR
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  value={selectedYear}
                  onChange={(event) => {
                    setSelectedYear(event.target.value);
                    setPage(0);
                  }}
                  MenuProps={menuProps}
                  sx={selectStyles}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* ORGANIZATION — read-only, from session */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                ORGANIZATION
              </Typography>

              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  px: 2,
                  py: 1.4,
                  backgroundColor: theme.palette.action.hover,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: textPrimary }}
                >
                  {currentUser?.organization_name || organizationId || "-"}
                </Typography>
              </Box>
            </Box>

            {/* STATUS — client-side, no is_active param
                on the list endpoint */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                STATUS
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  value={filterStatus}
                  onChange={(event) => {
                    setFilterStatus(event.target.value);
                    setPage(0);
                  }}
                  displayEmpty
                  MenuProps={menuProps}
                  sx={selectStyles}
                >
                  <MenuItem value="">All Statuses</MenuItem>

                  <MenuItem value="true">Active</MenuItem>

                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Button
            variant="text"
            onClick={handleResetFilter}
            sx={{
              textTransform: "none",
              color: textPrimary,
            }}
          >
            Reset
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              setPage(0);
              setFilterModalOpen(false);
              fetchBalances();
            }}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          TABLE
      =================================================== */}

      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <CardContent
          sx={{
            p: 0,
            "&:last-child": { pb: 0 },
          }}
        >
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              overflowX: "auto",
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Table sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    "& .MuiTableCell-root": {
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: "0.82rem",
                      borderBottom: "none",
                      py: 1.8,
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>S.No.</TableCell>

                  <TableCell>Employee</TableCell>

                  <TableCell>Leave Type</TableCell>

                  <TableCell>Branch</TableCell>

                  <TableCell>Year</TableCell>

                  <TableCell>Allocated</TableCell>

                  <TableCell>Used</TableCell>

                  <TableCell>Remaining</TableCell>

                  <TableCell>Status</TableCell>

                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      align="center"
                      sx={{ py: 7, borderBottom: "none" }}
                    >
                      <CircularProgress
                        size={32}
                        sx={{ color: theme.palette.primary.main }}
                      />

                      <Typography sx={{ mt: 1.5, color: textPrimary }}>
                        Loading leave balances...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : displayedBalances.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      align="center"
                      sx={{ py: 8, borderBottom: "none" }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          color: textPrimary,
                        }}
                      >
                        No leave balances found
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ mt: 0.7, color: textPrimary }}
                      >
                        Create a leave balance to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedBalances.map((balance, index) => (
                    <TableRow
                      key={balance.id}
                      hover
                      sx={{
                        transition: "background-color 0.2s ease",
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                        "& .MuiTableCell-root": {
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          py: 1.5,
                          fontSize: "0.82rem",
                          color: textPrimary,
                        },
                      }}
                    >
                      <TableCell
                        sx={{ fontWeight: 600, color: textPrimary }}
                      >
                        {page * rowsPerPage + index + 1}
                      </TableCell>

                      {/* EMPLOYEE */}

                      <TableCell>
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.83rem",
                              color: textPrimary,
                            }}
                          >
                            {balance.employee_name || "-"}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{
                              color: textPrimary,
                              fontSize: "0.72rem",
                            }}
                          >
                            {balance.employee_code || "-"}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* LEAVE TYPE */}

                      <TableCell>
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.82rem",
                              color: textPrimary,
                            }}
                          >
                            {balance.leave_type_name || "-"}
                          </Typography>

                          {balance.leave_code && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: textPrimary,
                                fontSize: "0.7rem",
                              }}
                            >
                              {balance.leave_code}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      {/* BRANCH — uses the API's branch_name */}

                      <TableCell>
                        <Typography
                          sx={{ fontWeight: 600, color: textPrimary }}
                        >
                          {balance.branch_name || "-"}
                        </Typography>
                      </TableCell>

                      {/* YEAR */}

                      <TableCell>
                        <Typography sx={{ fontWeight: 600, color: textPrimary }}>
                          {balance.year || "-"}
                        </Typography>
                      </TableCell>

                      {/* ALLOCATED */}

                      <TableCell>
                        <Typography sx={{ fontWeight: 600, color: textPrimary }}>
                          {Number(balance.allocated_days || 0).toFixed(2)}
                        </Typography>
                      </TableCell>

                      {/* USED */}

                      <TableCell>
                        <Typography sx={{ fontWeight: 600, color: textPrimary }}>
                          {Number(balance.used_days || 0).toFixed(2)}
                        </Typography>
                      </TableCell>

                      {/* REMAINING */}

                      <TableCell>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color:
                              Number(balance.remaining_days || 0) > 0
                                ? theme.palette.success.main
                                : textPrimary,
                          }}
                        >
                          {Number(balance.remaining_days || 0).toFixed(2)}
                        </Typography>
                      </TableCell>

                      {/* STATUS */}

                      <TableCell>
                        <Chip
                          size="small"
                          label={balance.is_active ? "Active" : "Inactive"}
                          sx={{
                            height: 26,
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            borderRadius: "6px",
                            backgroundColor: balance.is_active
                              ? isDark
                                ? "rgba(34,197,94,0.18)"
                                : "#DCFCE7"
                              : isDark
                              ? "rgba(148,163,184,0.18)"
                              : "#F1F5F9",
                            color: balance.is_active
                              ? isDark
                                ? "#4ADE80"
                                : "#15803D"
                              : isDark
                              ? "#CBD5E1"
                              : "#64748B",
                          }}
                        />
                      </TableCell>

                      {/* ACTIONS */}

                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 0.7,
                          }}
                        >
                          {/* STATUS SWITCH */}

                          <Tooltip
                            title={balance.is_active ? "Deactivate" : "Activate"}
                          >
                            <Switch
                              size="small"
                              checked={Boolean(balance.is_active)}
                              onChange={() =>
                                handleOpenStatusConfirmation(balance)
                              }
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                  color: theme.palette.primary.main,
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                  {
                                    backgroundColor:
                                      theme.palette.primary.main,
                                  },
                              }}
                            />
                          </Tooltip>

                          {/* EDIT */}

                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(balance)}
                              sx={{
                                width: 32,
                                height: 32,
                                color: theme.palette.primary.main,
                                backgroundColor: isDark
                                  ? `${theme.palette.primary.main}22`
                                  : `${theme.palette.primary.main}12`,
                                "&:hover": {
                                  backgroundColor: theme.palette.primary.main,
                                  color: "#FFFFFF",
                                },
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* DELETE */}

                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDelete(balance.id)}
                              sx={{
                                width: 32,
                                height: 32,
                                color: theme.palette.error.main,
                                backgroundColor: isDark
                                  ? `${theme.palette.error.main}22`
                                  : `${theme.palette.error.main}12`,
                                "&:hover": {
                                  backgroundColor: theme.palette.error.main,
                                  color: "#FFFFFF",
                                },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* PAGINATION — client-side, the list endpoint
              returns a plain array */}

          <TablePagination
            component="div"
            count={filteredBalances.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              color: textPrimary,
              "& .MuiTablePagination-selectIcon": {
                color: textPrimary,
              },
              "& .MuiTablePagination-actions button": {
                color: textPrimary,
              },
            }}
          />
        </CardContent>
      </Card>

      {/* ===================================================
          CREATE / EDIT MODAL
      =================================================== */}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{ color: textPrimary }}
                          >
                            Add/Edit Leave Balance
                          </Typography>
            <IconButton
              size="small"
              onClick={handleCloseDialog}
              disabled={saving}
              sx={{
                color: textPrimary,
                border: `1px solid ${theme.palette.divider}`,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent
          sx={{
            px: 3,
            py: 3,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            {/* EMPLOYEE */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: isDark ? theme.palette.grey[300] : "#111827",
                }}
              >
                EMPLOYEE
              </Typography>

              <Box
                onClick={handleOpenEmployeeModal}
                sx={{
                  minHeight: 40,
                  border: `1px solid ${borderColor}`,
                  borderRadius: 2,
                  backgroundColor: inputBackground,
                  display: "flex",
                  alignItems: "center",
                  px: 1.7,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    color: textPrimary,
                    fontWeight: formData.employee ? 600 : 400,
                  }}
                >
                  {formData.employee
                    ? (() => {
                        const selectedEmployee = employees.find(
                          (employee) =>
                            Number(employee.id) === Number(formData.employee)
                        );

                        return selectedEmployee
                          ? `${getEmployeeLabel(selectedEmployee)}${
                              selectedEmployee.employee_code
                                ? ` (${selectedEmployee.employee_code})`
                                : ""
                            }`
                          : "Select Employee";
                      })()
                    : "Select Employee"}
                </Typography>
              </Box>
            </Box>

            {/* LEAVE TYPE */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: theme.palette.primary.main,
                }}
              >
                LEAVE TYPE
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  name="leave_type"
                  value={formData.leave_type}
                  onChange={handleFormChange}
                  displayEmpty
                  MenuProps={menuProps}
                  sx={selectStyles}
                >
                  <MenuItem value="">Select Leave Type</MenuItem>

                  {leaveTypes.map((leaveType) => (
                    <MenuItem key={leaveType.id} value={leaveType.id}>
                      {getLeaveTypeLabel(leaveType)}

                      {leaveType.leave_code && ` (${leaveType.leave_code})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* YEAR */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: theme.palette.primary.main,
                }}
              >
                YEAR
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  name="year"
                  value={formData.year}
                  onChange={handleFormChange}
                  MenuProps={menuProps}
                  sx={selectStyles}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* ALLOCATED DAYS — $decimal, sent as a string */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: theme.palette.primary.main,
                }}
              >
                ALLOCATED DAYS
              </Typography>

              <TextField
                fullWidth
                size="small"
                type="number"
                name="allocated_days"
                value={formData.allocated_days}
                onChange={handleFormChange}
                placeholder="e.g. 22"
                inputProps={{ min: 0, step: "0.5" }}
                sx={textFieldStyles}
              />
            </Box>
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1.2,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Button
            variant="contained"
            onClick={handleCloseDialog}
            disabled={saving}
            data-app-cancel="true"
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Save fontSize="small" />
              )
            }
            onClick={handleSave}
            disabled={saving}
            sx={{
              minWidth: 150,
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            {saving ? "Saving..." : editMode ? "Update Balance" : "Create Balance"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          BULK ALLOCATE MODAL
      =================================================== */}

      <Dialog
        open={bulkModalOpen}
        onClose={() => {
          if (!saving) setBulkModalOpen(false);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: textPrimary }}>
                Bulk Allocate Leave Balance
              </Typography>

              <Typography
                variant="body2"
                sx={{ mt: 0.4, color: textSecondary }}
              >
                Allocates to all active employees of the selected organization
                and branch for the chosen leave type and year.
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={() => setBulkModalOpen(false)}
              disabled={saving}
              sx={{
                color: textPrimary,
                border: `1px solid ${theme.palette.divider}`,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent
          sx={{
            px: 3,
            py: 3,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            {/* LEAVE TYPE */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: theme.palette.primary.main,
                }}
              >
                LEAVE TYPE
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  name="leave_type"
                  value={bulkForm.leave_type}
                  onChange={handleBulkFormChange}
                  displayEmpty
                  MenuProps={menuProps}
                  sx={selectStyles}
                >
                  <MenuItem value="">Select Leave Type</MenuItem>

                  {leaveTypes.map((leaveType) => (
                    <MenuItem key={leaveType.id} value={leaveType.id}>
                      {getLeaveTypeLabel(leaveType)}

                      {leaveType.leave_code && ` (${leaveType.leave_code})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* YEAR */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: theme.palette.primary.main,
                }}
              >
                YEAR
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  name="year"
                  value={bulkForm.year}
                  onChange={handleBulkFormChange}
                  MenuProps={menuProps}
                  sx={selectStyles}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* ALLOCATED DAYS — number per the bulk-create body */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: theme.palette.primary.main,
                }}
              >
                ALLOCATED DAYS
              </Typography>

              <TextField
                fullWidth
                size="small"
                type="number"
                name="allocated_days"
                value={bulkForm.allocated_days}
                onChange={handleBulkFormChange}
                placeholder="e.g. 22"
                inputProps={{ min: 0, step: "0.5" }}
                sx={textFieldStyles}
              />
            </Box>
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1.2,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Button
            variant="contained"
            onClick={() => setBulkModalOpen(false)}
            disabled={saving}
            data-app-cancel="true"
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <BulkAllocateIcon fontSize="small" />
              )
            }
            onClick={handleBulkAllocate}
            disabled={saving}
            sx={{
              minWidth: 170,
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            {saving ? "Allocating..." : "Bulk Allocate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          EMPLOYEE SELECTION MODAL
      =================================================== */}

      <Dialog
        open={employeeModalOpen}
        onClose={handleCloseEmployeeModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: textPrimary }}>
                Select Employee
              </Typography>

              <Typography variant="body2" sx={{ mt: 0.4, color: textSecondary }}>
                Search and select an employee
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={handleCloseEmployeeModal}
              sx={{
                color: textPrimary,
                border: `1px solid ${theme.palette.divider}`,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent
          sx={{
            p: 3,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {/* SEARCH */}

          <TextField
            fullWidth
            size="small"
            value={employeeSearch}
            onChange={(event) => setEmployeeSearch(event.target.value)}
            placeholder="Search employee, code, department..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: theme.palette.primary.main }} />
                </InputAdornment>
              ),
              endAdornment:
                employeeSearch && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setEmployeeSearch("")}
                      sx={{ color: textPrimary }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
            }}
            sx={{
              mb: 2.5,
              "& .MuiOutlinedInput-root": {
                height: 46,
                borderRadius: 2,
                color: textPrimary,
                backgroundColor: inputBackground,
                "& fieldset": {
                  borderColor,
                },
                "&:hover fieldset": {
                  borderColor: theme.palette.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                },
              },
              "& input": {
                color: textPrimary,
                WebkitTextFillColor: textPrimary,
              },
              "& input::placeholder": {
                color: textPrimary,
                opacity: 0.8,
              },
            }}
          />

          {/* EMPLOYEE LIST */}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.2,
              maxHeight: 430,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {filteredEmployees.length === 0 ? (
              <Box
                sx={{
                  py: 7,
                  textAlign: "center",
                  border: `1px dashed ${theme.palette.divider}`,
                  borderRadius: 2,
                }}
              >
                <Typography fontWeight={700} sx={{ color: textPrimary }}>
                  No employees found
                </Typography>

                <Typography variant="body2" sx={{ mt: 0.5, color: textPrimary }}>
                  Try a different employee name or code.
                </Typography>
              </Box>
            ) : (
              filteredEmployees.map((employee) => {
                const selected =
                  Number(formData.employee) === Number(employee.id);

                return (
                  <Box
                    key={employee.id}
                    onClick={() => handleSelectEmployee(employee)}
                    sx={{
                      border: `1px solid ${
                        selected
                          ? theme.palette.primary.main
                          : theme.palette.divider
                      }`,
                      borderRadius: 2,
                      p: 1.8,
                      cursor: "pointer",
                      backgroundColor: selected
                        ? isDark
                          ? `${theme.palette.primary.main}18`
                          : `${theme.palette.primary.main}08`
                        : theme.palette.background.paper,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        {/* NAME + CODE */}

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.9rem",
                              color: textPrimary,
                            }}
                          >
                            {getEmployeeLabel(employee)}
                          </Typography>

                          {employee.employee_code && (
                            <Chip
                              size="small"
                              label={employee.employee_code}
                              sx={{
                                height: 23,
                                fontSize: "0.68rem",
                                fontWeight: 700,
                                borderRadius: "5px",
                                backgroundColor: isDark
                                  ? "rgba(255,255,255,0.08)"
                                  : "rgba(0,0,0,0.05)",
                                color: textPrimary,
                              }}
                            />
                          )}
                        </Box>

                        {/* DETAILS */}

                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1.5,
                            mt: 1,
                          }}
                        >
                          {employee.department_name && (
                            <Typography
                              variant="caption"
                              sx={{ color: textPrimary }}
                            >
                              <strong>Department:</strong>{" "}
                              {employee.department_name}
                            </Typography>
                          )}

                          {employee.designation_name && (
                            <Typography
                              variant="caption"
                              sx={{ color: textPrimary }}
                            >
                              <strong>Designation:</strong>{" "}
                              {employee.designation_name}
                            </Typography>
                          )}

                          {employee.branch_name && (
                            <Typography
                              variant="caption"
                              sx={{ color: textPrimary }}
                            >
                              <strong>Branch:</strong> {employee.branch_name}
                            </Typography>
                          )}
                        </Box>

                        {/* EMAIL */}

                        {employee.email && (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", mt: 0.7, color: textPrimary }}
                          >
                            {employee.email}
                          </Typography>
                        )}
                      </Box>

                      {selected && (
                        <Chip
                          size="small"
                          label="Selected"
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                            backgroundColor: isDark
                              ? `${theme.palette.primary.main}22`
                              : `${theme.palette.primary.main}12`,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Button
            variant="contained"
            onClick={handleCloseEmployeeModal}
            data-app-cancel="true"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          DELETE MODAL
      =================================================== */}

      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDelete}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: textPrimary,
            fontWeight: 700,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          Delete Leave Balance
        </DialogTitle>

        <Divider />

        <DialogContent
          sx={{
            py: 3,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography sx={{ color: textPrimary }}>
            Are you sure you want to delete this leave balance?
          </Typography>

          <Typography variant="body2" sx={{ mt: 1, color: textPrimary }}>
            This action will remove the employee's leave balance record.
          </Typography>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Button
            variant="contained"
            onClick={handleCloseDelete}
            disabled={saving}
            data-app-cancel="true"
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Delete />
              )
            }
            onClick={handleDelete}
            disabled={saving}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            {saving ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          ACTIVE / INACTIVE CONFIRMATION MODAL
      =================================================== */}

      <Dialog
        open={statusModalOpen}
        onClose={handleCloseStatusConfirmation}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: textPrimary,
            fontWeight: 700,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {statusBalance?.is_active
            ? "Deactivate Leave Balance"
            : "Activate Leave Balance"}
        </DialogTitle>

        <Divider />

        <DialogContent
          sx={{
            py: 3,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography sx={{ color: textPrimary, fontWeight: 600 }}>
            {statusBalance?.is_active
              ? "Are you sure you want to deactivate this leave balance?"
              : "Are you sure you want to activate this leave balance?"}
          </Typography>

          {statusBalance && (
            <Box
              sx={{
                mt: 2,
                p: 1.8,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                backgroundColor: theme.palette.action.hover,
              }}
            >
              <Typography sx={{ fontWeight: 700, color: textPrimary }}>
                {statusBalance.employee_name || "-"}
              </Typography>

              <Typography
                variant="body2"
                sx={{ mt: 0.4, color: textPrimary }}
              >
                {statusBalance.employee_code || "-"}
                {" • "}
                {statusBalance.leave_type_name || "-"}
              </Typography>
            </Box>
          )}

          <Typography variant="body2" sx={{ mt: 1.5, color: textPrimary }}>
            {statusBalance?.is_active
              ? "The balance will remain in the table but will be marked as inactive."
              : "The balance will become active and available again."}
          </Typography>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Button
            variant="contained"
            onClick={handleCloseStatusConfirmation}
            disabled={statusSaving}
            data-app-cancel="true"
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color={statusBalance?.is_active ? "error" : "success"}
            onClick={handleConfirmStatusChange}
            disabled={statusSaving}
            startIcon={
              statusSaving ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              minWidth: 110,
            }}
          >
            {statusSaving
              ? "Updating..."
              : statusBalance?.is_active
              ? "Deactivate"
              : "Activate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          SNACKBAR
      =================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LeaveBalance;